import {TimelineLite} from "gsap";
import {DDay} from "../DDay";
import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {BlitzKriegParsedInitData} from "../server/data/BlitzKriegParsedInitData";
import {BlitzKriegParsedGameData} from "../server/data/BlitzKriegParsedGameData";
import {BlitzKriegGameMode} from "../server/BlitzKriegGameMode";
import {BlitzkriegSymbols} from "../symbol/DDaySymbolConfig";
import {JurisdictionNames} from "@nolimitcity/slot-launcher/bin/plugins/apiplugin/GameClientConfiguration";
import {LoopableList} from "../../core/utils/LoopableList";

export class CommonService {

    public static createTimelineForAnimation(spine: PIXI.spine.Spine, trackIndex: number, animationName: string, loop: boolean, animationAlpha: number = 1, mixDuration: number = 0, resetOnComplete: boolean = true): TimelineLite {
        const tl: TimelineLite = new TimelineLite({
            repeat: loop ? -1 : 0
        });

        // trying safe checks for #136
        if (!spine.spineData || !spine.state) {
            return tl;
        }

        const animation: PIXI.spine.core.Animation = spine.spineData.findAnimation(animationName);
        if (!animation) {
            throw new Error(`No animation named ${animationName}`);
        }

        spine.autoUpdate = false;

        const setAnimation: () => void = () => {
            // trying safe check for #136
            if (spine.state) {
                const trackEntry: PIXI.spine.core.TrackEntry = spine.state.setAnimationWith(trackIndex, animation, false);
                trackEntry.alpha = animationAlpha;
                trackEntry.mixDuration = mixDuration;
            }
        };

        const target = {
            playHead: 0,
            spine: spine,
            prevTime: 0
        };

        const updateFunc: () => void = () => {
            // trying safe checks for #136
            if (spine && target) {
                const now = target.playHead;
                spine.update(now - target.prevTime);
                target.prevTime = now;
            }
        };

        tl.add(() => {
            target.prevTime = 0;
            setAnimation();
        });
        tl.fromTo(target, {playHead: 0}, {
            duration: animation.duration,
            playHead: animation.duration,
            onUpdate: updateFunc,
            ease: "none"
        });
        if (resetOnComplete) {
            tl.add(() => {
                target.prevTime = 0;
                setAnimation();
            });
        }
        tl.to(target, {duration: 0.0001}); //Needed so gsap does not skip "setAnimation" during reverse play.
        tl.add(() => {
            target.playHead = animation.duration;
            updateFunc();
        });

        return tl;
    }

    public dimFireOverlay(enable: boolean = false, reelId: number, symId: number): TimelineLite {
        let tl: TimelineLite = new TimelineLite();
        let overlay: PIXI.spine.Spine = DDay.fireOverlays[reelId][symId];
        if (overlay) {
            tl.to(overlay, {
                alpha: enable ? 0.2 : 1,
                duration: 0.3
            }, 0);
        } else {
            tl.to(this, {duration: 0.01});
        }
        return tl;
    }

    public dimBulletOverlay(enable: boolean = false, reelId: number, symId: number): TimelineLite {
        let tl: TimelineLite = new TimelineLite();
        let overlay: PIXI.spine.Spine = DDay.bulletOverlays[reelId][symId];
        if (overlay) {
            tl.to(overlay, {
                alpha: enable ? 0.2 : 1,
                duration: 0.3
            }, 0);
        } else {
            tl.to(this, {duration: 0.01});
        }

        return tl;
    }

    public getAtomicWildIndex(multiplier: number): number {
        let index = 0;
        switch (multiplier) {
            case 10:
                index = 1;
                break;
            case 20:
                index = 2;
                break;
            case 30:
                index = 3;
                break;
            case 50:
                index = 4;
                break;
            case 100:
                index = 5;
                break;
        }
        return index;
    }

    public applyMaskOnReel(layer: StageLayer): void {
        let mask: PIXI.Graphics = new PIXI.Graphics();
        mask.beginFill(0xFF0000, 1);
        mask.drawPolygon(
            [
                new PIXI.Point(0, 300),
                new PIXI.Point(0, 654),
                new PIXI.Point(720, 654),
                new PIXI.Point(720, 300),
                new PIXI.Point(576, 300),
                new PIXI.Point(576, 182),
                new PIXI.Point(432, 182),
                new PIXI.Point(432, 64),
                new PIXI.Point(288, 64),
                new PIXI.Point(288, 182),
                new PIXI.Point(144, 182),
                new PIXI.Point(144, 300)
            ]
        );
        mask.endFill();
        layer.addChildAt(mask, 0);
        layer.mask = mask;
    }

    public getFSOrSFSName(isIntro: boolean = false): string {
        let featuresRevealed: number = 0;
        let spinName: string = "neptune";
        let data: BlitzKriegParsedInitData | BlitzKriegParsedGameData;
        if (DDay.model.isRestore) {
            data = DDay.model.parsedInitData;
        } else {
            data = DDay.model.parsedGameData;
        }
        if (data.nextMode !== BlitzKriegGameMode.FS_PICK) {
            data.fsPicks.forEach((featureEnable: number) => {
                if (featureEnable > -1) {
                    featuresRevealed++;
                }
            });
            featuresRevealed++;
        } else {
            data.reels.forEach((reel: string[]) => {
                reel.forEach((sym: string) => {
                    if (sym === BlitzkriegSymbols.S) {
                        featuresRevealed++;
                    }
                });
            })
        }
        if (featuresRevealed === 4) {
            spinName = "invasion";
        } else if (featuresRevealed === 5) {
            spinName = "overload";
        }
        spinName = (isIntro ? "intro" : "outro") + "_" + spinName;
        return spinName;
    }

    public isPortugal(): boolean {
        return DDay.apiPlugin.gameClientConfiguration.jurisdictionName === JurisdictionNames.PORTUGAL;
    }
}
